import { useState, useEffect, useCallback } from 'react'
import { Aircraft } from '../types/intellijetnet'
import { AircraftOverride, AircraftOverrideInput, AircraftTransaction, MarketEMP, MarketCustomColumn, ContainerType } from '../types/database'
import { OverridesService } from '../services/overrides.service'
import { MarketEMPsService } from '../services/market-emps.service'
import { MarketCustomColumnsService } from '../services/market-custom-columns.service'
import { TransactionOverridesService } from '../services/transaction-overrides.service'
import { aircraftToOverrideInput } from '../utils/overrides'
import { useOverrideRefresh } from '../contexts/OverrideRefreshContext'

export type OverrideFormData = AircraftOverrideInput & { sale_price?: number | null; external_sale_price?: number | null }

/** All the override fields that can be mapped from transaction or override sources */
const OVERRIDE_FIELDS = [
  'sale_price', 'external_sale_price', 'asking_price', 'take_price', 'take_price_modifier',
  'external_asking_price', 'external_take_price', 'external_take_price_modifier',
  'actual_ask_make_offer', 'adj_ask_make_offer', 'aftt', 'tbo', 'landings', 'pax',
  'base_airport', 'base_city', 'base_state', 'base_country', 'status',
  'maintenance_program', 'damage_history', 'emp', 'for_sale', 'leased',
  'date_listed', 'date_purchased', 'paint_date', 'interior_date',
  'custom_1_value', 'custom_2_value', 'custom_3_value',
  'owner', 'broker', 'owner_custom', 'broker_custom',
] as const

function pickOverrideFields(source: Record<string, any>): Partial<OverrideFormData> {
  const result: Record<string, any> = {}
  for (const key of OVERRIDE_FIELDS) {
    if (key in source) result[key] = source[key]
  }
  if (source.damage_history != null) {
    result.damage_history = source.damage_history as 'Yes' | 'No' | 'Unknown' | null
  }
  return result
}

interface UseOverrideFormOptions {
  isOpen: boolean
  aircraft: Aircraft
  containerType?: ContainerType
  transaction?: AircraftTransaction
  onSave?: (override: AircraftOverride | null) => void
  onClose: () => void
}

export function useOverrideForm({ isOpen, aircraft, containerType, transaction, onSave, onClose }: UseOverrideFormOptions) {
  const [formData, setFormData] = useState<OverrideFormData>({ aircraft_id: aircraft.aircraft_id })
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasExistingOverride, setHasExistingOverride] = useState(false)
  const [hasExistingTransactionOverride, setHasExistingTransactionOverride] = useState(false)
  const [availableEMPs, setAvailableEMPs] = useState<MarketEMP[]>([])
  const [customColumns, setCustomColumns] = useState<MarketCustomColumn[]>([])
  const [backfilling, setBackfilling] = useState(false)
  const { refreshOverrideForAircraft } = useOverrideRefresh()

  const loadAvailableEMPs = useCallback(async () => {
    try {
      if (aircraft.model_id) {
        const emps = await MarketEMPsService.getAllByModelId(aircraft.model_id)
        setAvailableEMPs(emps)
      }
    } catch { /* ignore */ }
  }, [aircraft.model_id])

  const loadCustomColumns = useCallback(async () => {
    try {
      if (aircraft.model_id) {
        const columns = await MarketCustomColumnsService.getByModelId(aircraft.model_id)
        setCustomColumns(columns)
      }
    } catch { /* ignore */ }
  }, [aircraft.model_id])

  const loadExistingTransactionOverride = useCallback(async () => {
    if (!transaction) return
    try {
      const transactionOverride = await TransactionOverridesService.getByTransactionId(transaction.transaction_id)
      if (transactionOverride) {
        setHasExistingTransactionOverride(true)
        setFormData(prev => ({ ...prev, ...pickOverrideFields(transactionOverride) }))
      } else {
        setHasExistingTransactionOverride(false)
        setFormData(prev => ({ ...prev, ...pickOverrideFields(transaction) }))
      }
    } catch { /* ignore */ }
  }, [transaction])

  const loadExistingOverride = useCallback(async () => {
    try {
      setError(null)
      const existingOverride = await OverridesService.getByAircraftId(aircraft.aircraft_id)
      if (existingOverride) {
        setHasExistingOverride(true)
        const overrideData: OverrideFormData = {
          aircraft_id: aircraft.aircraft_id,
          ...pickOverrideFields(existingOverride),
          sale_price: containerType === 'sold' && transaction ? transaction.sale_price : undefined,
          external_sale_price: containerType === 'sold' && transaction ? transaction.external_sale_price : undefined,
        }
        setFormData(overrideData)
      } else {
        setHasExistingOverride(false)
        const initialData = aircraftToOverrideInput(aircraft)
        setFormData({
          aircraft_id: aircraft.aircraft_id,
          ...initialData,
          sale_price: containerType === 'sold' && transaction ? transaction.sale_price : undefined,
          external_sale_price: containerType === 'sold' && transaction ? transaction.external_sale_price : undefined,
        })
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load override data')
    }
  }, [aircraft, containerType, transaction])

  useEffect(() => {
    if (isOpen) {
      loadExistingOverride()
      loadAvailableEMPs()
      loadCustomColumns()
      if (containerType === 'sold' && transaction) {
        loadExistingTransactionOverride()
      }
    }
  }, [isOpen, aircraft.aircraft_id, containerType, transaction, loadExistingOverride, loadAvailableEMPs, loadCustomColumns, loadExistingTransactionOverride])

  const handleInputChange = useCallback((field: keyof AircraftOverrideInput | 'sale_price' | 'external_sale_price', value: any) => {
    setFormData(prev => ({ ...prev, [field]: value === '' ? null : value }))
  }, [])

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setSaving(true)
      setError(null)
      if (containerType === 'sold' && transaction) {
        const transactionOverrideData = { transaction_id: transaction.transaction_id, ...pickOverrideFields(formData) }
        await TransactionOverridesService.updateOverrides(transaction.transaction_id, transactionOverrideData)
        onSave?.(null)
      } else {
        const { sale_price, external_sale_price, ...aircraftOverrideData } = formData
        const savedOverride = await OverridesService.upsert(aircraftOverrideData)
        refreshOverrideForAircraft(aircraft.aircraft_id)
        onSave?.(savedOverride)
      }
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to save override data')
    } finally {
      setSaving(false)
    }
  }, [formData, containerType, transaction, aircraft.aircraft_id, onSave, onClose, refreshOverrideForAircraft])

  const handleDelete = useCallback(async () => {
    if (!hasExistingOverride && !(containerType === 'sold' && hasExistingTransactionOverride)) return

    const confirmed = window.confirm(
      `Are you sure you want to delete all override data for aircraft ${aircraft.sernbr}? This will restore all fields to their original values.`
    )
    if (!confirmed) return

    try {
      setDeleting(true)
      setError(null)
      if (containerType === 'sold' && transaction && hasExistingTransactionOverride) {
        // TODO: Implement transaction override reset functionality
      } else if (hasExistingOverride) {
        await OverridesService.delete(aircraft.aircraft_id)
        refreshOverrideForAircraft(aircraft.aircraft_id)
      }
      onSave?.(null)
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to delete override data')
    } finally {
      setDeleting(false)
    }
  }, [hasExistingOverride, hasExistingTransactionOverride, containerType, transaction, aircraft, onSave, onClose, refreshOverrideForAircraft])

  const handleBackfillOverrides = useCallback(async () => {
    if (!transaction) return
    try {
      setBackfilling(true)
      setError(null)
      await TransactionOverridesService.backfillSnapshotWithOverrides(transaction.transaction_id)
      await loadExistingTransactionOverride()
      alert('Successfully backfilled snapshot with current override data!')
    } catch (err: any) {
      setError(err.message || 'Failed to backfill override data')
    } finally {
      setBackfilling(false)
    }
  }, [transaction, loadExistingTransactionOverride])

  const handleClose = useCallback(() => {
    if (!saving && !deleting && !backfilling) {
      onClose()
    }
  }, [saving, deleting, backfilling, onClose])

  return {
    formData,
    saving,
    deleting,
    backfilling,
    error,
    hasExistingOverride,
    hasExistingTransactionOverride,
    availableEMPs,
    customColumns,
    handleInputChange,
    handleSubmit,
    handleDelete,
    handleBackfillOverrides,
    handleClose,
  }
}
